/**
 * Hook to fetch user's enabled features from admin-controlled feature flags
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface UserFeatures {
  external_order_intake: boolean;
  customers_module: boolean;
}

interface UserFeatureRow {
  feature_name: string;
  enabled: boolean;
}

export function useUserFeatures() {
  const { user } = useAuth(false);

  return useQuery({
    queryKey: ["user-features", user?.id],
    queryFn: async (): Promise<UserFeatures> => {
      if (!user?.id) {
        return { external_order_intake: false, customers_module: false };
      }

      const { data, error } = await supabase
        .from("user_features")
        .select("feature_name, enabled")
        .eq("user_id", user.id);

      if (error) {
        console.error("Error fetching user features:", error);
        return { external_order_intake: false, customers_module: false };
      }

      // Build features object from rows
      const features: UserFeatures = {
        external_order_intake: false,
        customers_module: false,
      };

      (data as UserFeatureRow[] || []).forEach((row) => {
        if (row.feature_name === "external_order_intake" && row.enabled) {
          features.external_order_intake = true;
        }
        if (row.feature_name === "customers_module" && row.enabled) {
          features.customers_module = true;
        }
      });

      return features;
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
